/**
 * QL-f — recent changes to the identity fields everything else is hashed
 * against.
 *
 * A Verified ID and a PEP verdict both go stale the moment a name, national id,
 * country or date of birth changes — by design, since neither was made against
 * the new facts. Until this page existed, that happened with nothing on screen
 * to say what changed or who changed it, so a badge quietly disappearing looked
 * like a bug rather than the system working.
 */

/**
 * Where a change came from, in a reviewer's words.
 *
 * The two public sources are named as such rather than folded into "flow": a
 * change made by whoever held a flow link, with no signed-in user behind it, is
 * the one a reviewer most needs to pick out of a list.
 */
export const T = {
  en_GB: {
    sources: {
      client_editor: "Client editor",
      trapets_lookup: "PEP/Sanction form",
      sumsub_internal: "ID document (advisor)",
      sumsub_public: "ID document (client)",
      eid_login: "eID login (client)",
      flow_completion: "Flow completion",
      public_api: "Public API",
    },
    fields: {
      name: "Name",
      ssn: "National ID",
      country: "Country",
      dateOfBirth: "Date of birth",
      orgId: "Org number",
    } as Record<string, string>,
    via: (name: string) => `via ${name}`,
    representative: "representative",
    notSignedIn: "Not signed in",
    title: "Identity Changes",
    description:
      "Changes to the name, national ID, country and date of birth that screenings and ID verifications are checked against.",
    loading: "Loading…",
    loadFailed: "Could not load identity changes.",
    tryAgain: "Try again",
    empty: "No identity changes recorded.",
    columns: {
      when: "When",
      client: "Client",
      field: "Field",
      from: "From",
      to: "To",
      source: "Source",
      by: "By",
    },
  },
  nb_NO: {
    sources: {
      client_editor: "Kunderedigering",
      trapets_lookup: "PEP/sanksjonsskjema",
      sumsub_internal: "ID-dokument (rådgiver)",
      sumsub_public: "ID-dokument (kunde)",
      eid_login: "eID-innlogging (kunde)",
      flow_completion: "Fullført flyt",
      public_api: "Offentlig API",
    },
    fields: {
      name: "Navn",
      ssn: "Fødselsnummer",
      country: "Land",
      dateOfBirth: "Fødselsdato",
      orgId: "Organisasjonsnummer",
    } as Record<string, string>,
    via: (name: string) => `via ${name}`,
    representative: "representant",
    notSignedIn: "Ikke innlogget",
    title: "Identitetsendringer",
    description:
      "Endringer i navn, fødselsnummer, land og fødselsdato som screening og ID-verifisering kontrolleres mot.",
    loading: "Laster…",
    loadFailed: "Kunne ikke laste identitetsendringer.",
    tryAgain: "Prøv igjen",
    empty: "Ingen identitetsendringer registrert.",
    columns: {
      when: "Når",
      client: "Kunde",
      field: "Felt",
      from: "Fra",
      to: "Til",
      source: "Kilde",
      by: "Av",
    },
  },
} as const;
